"use client"

import {
  TradingSignerLinkApiError,
  getTradingSignerLink,
} from "@src/features/tradingSigner/onboarding/tradingSignerLinksAPI"
import { useWebAuthnCurrentCredential } from "@src/features/webauthn/hooks/useWebAuthnStore"
import { ChainType, useConnectWallet } from "@src/hooks/useConnectWallet"
import { walletIdentityFromSession } from "@src/utils/walletIdentity"
import { useQuery } from "@tanstack/react-query"
import { getAddress, isAddress } from "viem"

export function useTradingSignerAddress() {
  const currentPasskey = useWebAuthnCurrentCredential()
  const { state } = useConnectWallet()
  const sessionChainType = state.chainType
  const sessionAddress = state.address
  const walletIdentity = walletIdentityFromSession({
    isAuthorized: state.isAuthorized,
    chainType: sessionChainType,
    address: sessionAddress,
    webauthnPublicKey: currentPasskey?.publicKey,
  })
  const evmSessionAddress =
    sessionChainType === ChainType.EVM &&
    sessionAddress != null &&
    isAddress(sessionAddress)
      ? getAddress(sessionAddress)
      : undefined

  const signerLinkQuery = useQuery({
    queryKey: ["trading_signer_link", "readonly-address", walletIdentity],
    queryFn: async ({ signal }) => {
      if (walletIdentity == null) {
        return null
      }

      try {
        return await getTradingSignerLink(walletIdentity, signal)
      } catch (error) {
        if (
          error instanceof TradingSignerLinkApiError &&
          error.status === 404
        ) {
          return null
        }
        throw error
      }
    },
    enabled: walletIdentity != null,
    retry: false,
    staleTime: 60_000,
  })

  const resolvedAddress =
    signerLinkQuery.data?.address ??
    (signerLinkQuery.isSuccess ? evmSessionAddress : undefined)

  return {
    address: resolvedAddress,
    isLoading: signerLinkQuery.isLoading,
    isError: signerLinkQuery.isError,
  }
}
